/**
 * PDF zoom persistence, keyed per project.
 *
 * Zoom used to live in a single flat `page-scale` key, so it was one value
 * shared by every project: set 150% on one script, open another, and it opened
 * at 150% too — fix that one back to 100% and the first had lost its setting.
 *
 * Deliberately localStorage and NOT the user document. Zoom is a property of
 * the screen you are sitting at: the same account on a laptop and on a 27"
 * monitor wants different values, and syncing it across devices would make it
 * wrong on one of them. Contrast `titleFontSizeOptions`, which IS stored per
 * user per project on the user doc, because cue font size is a property of the
 * document rather than of the viewport.
 */

const FLAT_KEY = 'page-scale';
const MIN_SCALE = 0.5;
const MAX_SCALE = 4;

export function pageScaleKey(projectId) {
    return projectId ? `${FLAT_KEY}:${projectId}` : FLAT_KEY;
}

function parseScale(raw) {
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n) || n < MIN_SCALE || n > MAX_SCALE) {
        return null;
    }
    return n;
}

/**
 * Per-project value first, then the pre-migration flat key so nobody's current
 * zoom resets the first time they open a project, then 1.
 *
 * The flat-key fallback is READ-only and never written back under the old key,
 * so the shared value fades out as each project is opened and saved once.
 */
export function readPageScale(projectId) {
    let stored = null;
    try {
        if (projectId) {
            stored = parseScale(localStorage.getItem(pageScaleKey(projectId)));
        }
        if (stored === null) {
            stored = parseScale(localStorage.getItem(FLAT_KEY));
        }
    } catch (e) {
        // Private browsing / storage disabled: fall through to the default.
        stored = null;
    }
    return stored === null ? 1 : stored;
}

export function writePageScale(projectId, scale) {
    const n = parseScale(scale);
    try {
        localStorage.setItem(pageScaleKey(projectId), n === null ? 1 : n);
    } catch (e) {
        // Nothing to do — zoom simply will not persist for this session.
    }
}

export default { pageScaleKey, readPageScale, writePageScale };



// WEBPACK FOOTER //
// ./src/lib/page-scale-storage.js